import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const sectionStyle = {
  marginBottom: 32,
  padding: 20,
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
};

const primaryBtn = {
  padding: "10px 20px",
  backgroundColor: "#4CAF50",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const emptyNotice = { name: "", downloadUrl: "", order: "", isActive: true, file: null };
const emptySocial = { name: "", href: "", icon: "facebook", order: "" };

const ICON_OPTIONS = [
  { value: "facebook", label: "Facebook" },
  { value: "youtube", label: "Youtube" },
  { value: "twitter", label: "Twitter / X" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
];

const TopBar = () => {
  const [settings, setSettings] = useState({ phone: "", email: "" });
  const [notices, setNotices] = useState([]);
  const [socials, setSocials] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [editingSocialId, setEditingSocialId] = useState(null);
  const [noticeForm, setNoticeForm] = useState(emptyNotice);
  const [socialForm, setSocialForm] = useState(emptySocial);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setFetchLoading(true);
    try {
      const [topBarRes, noticesRes, socialsRes] = await Promise.all([
        axiosInstance.get("top-bar"),
        axiosInstance.get("top-bar/notices/all"),
        axiosInstance.get("top-bar/socials/all"),
      ]);
      const data = topBarRes.data?.data || {};
      setSettings({
        phone: data.phone || "",
        email: data.email || "",
      });
      setNotices(noticesRes.data?.data || []);
      setSocials(socialsRes.data?.data || []);
    } catch (error) {
      console.error("Error fetching top bar:", error);
    }
    setFetchLoading(false);
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    setSettingsLoading(true);
    try {
      await axiosInstance.post("top-bar/settings", {
        phone: settings.phone,
        email: settings.email,
        _method: "PUT",
      });
      Swal.fire("Success!", "Contact info updated successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update contact info",
        "error"
      );
    }
    setSettingsLoading(false);
  };

  // ── Notices ──────────────────────────────────────────────

  const openCreateNotice = () => {
    setEditingNoticeId(null);
    setNoticeForm(emptyNotice);
    setShowNoticeModal(true);
  };

  const openEditNotice = async (id) => {
    try {
      setEditingNoticeId(id);
      setShowNoticeModal(true);
      const response = await axiosInstance.get(`top-bar/notices/single/${id}`);
      const data = response?.data?.data;
      setNoticeForm({
        name: data?.name || "",
        downloadUrl: data?.downloadUrl || "",
        order: data?.order ?? "",
        isActive: data?.isActive !== false,
        file: null,
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load notice", "error");
    }
  };

  const handleNoticeSubmit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", noticeForm.name);
      if (noticeForm.downloadUrl) {
        formDataToSend.append("downloadUrl", noticeForm.downloadUrl);
      }
      if (noticeForm.order !== "" && noticeForm.order !== null) {
        formDataToSend.append("order", String(Number(noticeForm.order)));
      }
      formDataToSend.append("isActive", noticeForm.isActive ? "true" : "false");
      if (noticeForm.file) {
        formDataToSend.append("file", noticeForm.file);
      }

      if (editingNoticeId) {
        formDataToSend.append("_method", "PUT");
        await axiosInstance.post(
          `top-bar/notices/change/${editingNoticeId}`,
          formDataToSend
        );
        Swal.fire("Success!", "Notice updated successfully!", "success");
      } else {
        await axiosInstance.post("top-bar/notices/create", formDataToSend);
        Swal.fire("Success!", "Notice created successfully!", "success");
      }
      setShowNoticeModal(false);
      setNoticeForm(emptyNotice);
      await fetchAll();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save notice",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleNoticeDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`top-bar/notices/delete/${id}`);
      await fetchAll();
      Swal.fire("Deleted!", "Notice deleted successfully", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete notice",
        "error"
      );
    }
  };

  // ── Socials ──────────────────────────────────────────────

  const openCreateSocial = () => {
    setEditingSocialId(null);
    setSocialForm(emptySocial);
    setShowSocialModal(true);
  };

  const openEditSocial = async (id) => {
    try {
      setEditingSocialId(id);
      setShowSocialModal(true);
      const response = await axiosInstance.get(`top-bar/socials/single/${id}`);
      const data = response?.data?.data;
      setSocialForm({
        name: data?.name || "",
        href: data?.href || "",
        icon: data?.icon || "facebook",
        order: data?.order ?? "",
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load social link", "error");
    }
  };

  const handleSocialSubmit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const payload = {
        name: socialForm.name,
        href: socialForm.href,
        icon: socialForm.icon,
      };
      if (socialForm.order !== "" && socialForm.order !== null) {
        payload.order = Number(socialForm.order);
      }

      if (editingSocialId) {
        await axiosInstance.post(`top-bar/socials/change/${editingSocialId}`, {
          ...payload,
          _method: "PUT",
        });
        Swal.fire("Success!", "Social link updated successfully!", "success");
      } else {
        await axiosInstance.post("top-bar/socials/create", payload);
        Swal.fire("Success!", "Social link created successfully!", "success");
      }
      setShowSocialModal(false);
      setSocialForm(emptySocial);
      await fetchAll();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save social link",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleSocialDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`top-bar/socials/delete/${id}`);
      await fetchAll();
      Swal.fire("Deleted!", "Social link deleted successfully", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete social link",
        "error"
      );
    }
  };

  if (fetchLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: 8 }}>Top Bar</h2>
      <p style={{ marginTop: 0, marginBottom: 24, color: "#666" }}>
        Manage the site header contact info, NOTICE dropdown, and social media icons.
      </p>

      {/* Contact info */}
      <div style={sectionStyle}>
        <h3 style={{ marginTop: 0 }}>Contact Info</h3>
        <p style={{ marginTop: 0, color: "#666", fontSize: 14 }}>
          Phone and email shown on the left side of the top bar.
        </p>
        <form onSubmit={handleSettingsSave}>
          <div style={{ marginBottom: 12, maxWidth: 420 }}>
            <label style={labelStyle}>Phone</label>
            <input
              type="text"
              value={settings.phone}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, phone: e.target.value }))
              }
              placeholder="+92-44-2636060"
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 12, maxWidth: 420 }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="info@uo.edu.pk"
              style={fieldStyle}
            />
          </div>
          <button type="submit" style={primaryBtn}>
            {settingsLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Contact Info"
            )}
          </button>
        </form>
      </div>

      {/* Notices */}
      <div style={sectionStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>NOTICE Items</h3>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 14 }}>
              Items shown in the red NOTICE dropdown on the top bar.
            </p>
          </div>
          <button onClick={openCreateNotice} style={primaryBtn}>
            Add Notice
          </button>
        </div>

        {notices.length === 0 ? (
          <p style={{ color: "#888" }}>No notices yet. Add one to show the NOTICE button.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {notices.map((item) => (
              <div
                key={item._id}
                style={{
                  width: 280,
                  padding: 15,
                  borderRadius: 8,
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <h4 style={{ marginTop: 0, marginBottom: 8 }}>{item.name}</h4>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "#4a90e2", wordBreak: "break-all" }}>
                  {item.downloadUrl || "—"}
                </p>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "#666" }}>
                  Order: {item.order ?? 0} ·{" "}
                  <span style={{ color: item.isActive ? "#16a34a" : "#dc2626" }}>
                    {item.isActive ? "Active" : "Hidden"}
                  </span>
                </p>
                <Actions
                  courseId={item._id}
                  onEdit={openEditNotice}
                  onDelete={handleNoticeDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Social links */}
      <div style={sectionStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Social Media</h3>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 14 }}>
              Icons on the right side of the top bar.
            </p>
          </div>
          <button onClick={openCreateSocial} style={primaryBtn}>
            Add Social Link
          </button>
        </div>

        {socials.length === 0 ? (
          <p style={{ color: "#888" }}>No social links yet.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {socials.map((item) => (
              <div
                key={item._id}
                style={{
                  width: 280,
                  padding: 15,
                  borderRadius: 8,
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <h4 style={{ marginTop: 0, marginBottom: 8 }}>
                  {item.name}{" "}
                  <span style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>
                    ({item.icon})
                  </span>
                </h4>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "#4a90e2", wordBreak: "break-all" }}>
                  {item.href}
                </p>
                <p style={{ margin: "0 0 8px", fontSize: 13, color: "#666" }}>
                  Order: {item.order ?? 0}
                </p>
                <Actions
                  courseId={item._id}
                  onEdit={openEditSocial}
                  onDelete={handleSocialDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notice modal */}
      <Modal
        isOpen={showNoticeModal}
        onRequestClose={() => {
          setShowNoticeModal(false);
          setNoticeForm(emptyNotice);
        }}
        style={customModalStyles}
        contentLabel="Notice"
      >
        <h2>{editingNoticeId ? "Edit Notice" : "Add Notice"}</h2>
        <form onSubmit={handleNoticeSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={noticeForm.name}
              onChange={(e) =>
                setNoticeForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder="e.g. List of Applicants..."
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Download URL (optional if uploading a file)</label>
            <input
              type="text"
              value={noticeForm.downloadUrl}
              onChange={(e) =>
                setNoticeForm((prev) => ({ ...prev, downloadUrl: e.target.value }))
              }
              placeholder="/list/... or https://..."
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Upload file (PDF/DOC)</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) =>
                setNoticeForm((prev) => ({
                  ...prev,
                  file: e.target.files?.[0] || null,
                }))
              }
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Order</label>
            <input
              type="number"
              min="0"
              value={noticeForm.order}
              onChange={(e) =>
                setNoticeForm((prev) => ({ ...prev, order: e.target.value }))
              }
              placeholder="Lower numbers appear first"
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={noticeForm.isActive}
                onChange={(e) =>
                  setNoticeForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              Active (show on website)
            </label>
          </div>
          <button type="submit" style={primaryBtn}>
            {buttonLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : editingNoticeId ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </form>
      </Modal>

      {/* Social modal */}
      <Modal
        isOpen={showSocialModal}
        onRequestClose={() => {
          setShowSocialModal(false);
          setSocialForm(emptySocial);
        }}
        style={customModalStyles}
        contentLabel="Social Link"
      >
        <h2>{editingSocialId ? "Edit Social Link" : "Add Social Link"}</h2>
        <form onSubmit={handleSocialSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Name</label>
            <input
              type="text"
              value={socialForm.name}
              onChange={(e) =>
                setSocialForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder="e.g. Facebook"
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>URL</label>
            <input
              type="url"
              value={socialForm.href}
              onChange={(e) =>
                setSocialForm((prev) => ({ ...prev, href: e.target.value }))
              }
              required
              placeholder="https://..."
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Icon</label>
            <select
              value={socialForm.icon}
              onChange={(e) =>
                setSocialForm((prev) => ({ ...prev, icon: e.target.value }))
              }
              style={fieldStyle}
            >
              {ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Order</label>
            <input
              type="number"
              min="0"
              value={socialForm.order}
              onChange={(e) =>
                setSocialForm((prev) => ({ ...prev, order: e.target.value }))
              }
              placeholder="Lower numbers appear first"
              style={fieldStyle}
            />
          </div>
          <button type="submit" style={primaryBtn}>
            {buttonLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : editingSocialId ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default TopBar;
